import React, { useEffect, useMemo, useState } from 'react';

import CardContent from '@dashboard/components/CardContent';
import CardListItem from '@dashboard/components/CardListItem';
import CardListItemElement from '@dashboard/components/CardListItemElement';
import ExportView from '@dashboard/components/ExportView';
import SearchInput from '@dashboard/components/Form/components/SearchInput';
import SortInput from '@dashboard/components/Form/components/SortInput';
import FilterInput from '@dashboard/components/Form/components/FilterInput';

import useUrlQuery from '@/dashboard/hooks/useUrlQuery';
import GuestsList from '../components/GuestsList';
import { stringify } from 'query-string';
import { useHistory } from 'react-router';

const SORT_DIRECTIONS = {
  ASCENDING: '1',
  DESCENDING: '-1',
};

const SORT_OPTIONS = {
  NEWEST_FIRST: {
    value: 'orderedAt',
    direction: SORT_DIRECTIONS.DESCENDING,
    label: 'Newest first',
  },
  OLDEST_FIRST: {
    value: 'orderedAt',
    direction: SORT_DIRECTIONS.ASCENDING,
    label: 'Oldest first',
  },
  NAME_ASCENDING: {
    value: 'customerName',
    direction: SORT_DIRECTIONS.ASCENDING,
    label: 'Name A-Z',
  },
  NAME_DESCENDING: {
    value: 'customerName',
    direction: SORT_DIRECTIONS.DESCENDING,
    label: 'Name Z-A',
  },
  EMAIL_ASCENDING: {
    value: 'customerEmail',
    direction: SORT_DIRECTIONS.ASCENDING,
    label: 'Email A-Z',
  },
  EMAIL_DESCENDING: {
    value: 'customerEmail',
    direction: SORT_DIRECTIONS.DESCENDING,
    label: 'Email Z-A',
  },
};

function getSortOptionFromQuery(sortBy, sortDirection) {
  const sortOption = Object.keys(SORT_OPTIONS).find(
    key => SORT_OPTIONS[key].value === sortBy && SORT_OPTIONS[key].direction === sortDirection,
  );

  return sortOption || Object.keys(SORT_OPTIONS)[0];
}

const PageGuests = ({ page }) => {
  const query = useUrlQuery();
  const history = useHistory();

  const [sortOption, setSortOption] = useState(
    getSortOptionFromQuery(query.sortBy, query.sortDirection),
  );
  const [search, setSearch] = useState(query.search || '');
  const [ticketGroupFilter, setTicketGroupFilter] = useState(query.ticketGroup || 'ALL');
  const [ticketTypeFilter, setTicketTypeFilter] = useState(query.ticketType || 'ALL');
  const [guestStatusFilter, setGuestStatusFilter] = useState(query.guestStatus || 'ALL');
  const [guestCheckInStatusFilter, setGuestCheckInStatusFilter] = useState(
    query.guestCheckInStatus || 'ALL',
  );

  const ticketGroupOptions = useMemo(
    () => ({
      ALL: {
        value: '',
        label: 'All ticket groups',
      },
      ...(page.ticketGroups?.reduce((acc, group) => {
        acc[group._id] = {
          value: group._id,
          label: group.name,
        };
        return acc;
      }, {}) || {}),
    }),
    [page],
  );

  const ticketTypeOptions = useMemo(
    () => ({
      ALL: {
        value: '',
        label: 'All tickets',
      },
      ...(page.ticketGroups?.reduce((acc, group) => {
        // If a specific ticket group is selected, only show ticket types from that group
        if (
          ticketGroupFilter !== 'ALL' &&
          group._id !== ticketGroupOptions[ticketGroupFilter]?.value
        ) {
          return acc;
        }

        group.ticketTypes?.forEach(ticketType => {
          acc[ticketType._id] = {
            value: ticketType._id,
            label: `${ticketType.name} (${group.name})`,
          };
        });
        return acc;
      }, {}) || {}),
    }),
    [page, ticketGroupFilter, ticketGroupOptions],
  );

  const guestStatusOptions = {
    ALL: {
      value: '',
      label: 'All statuses',
    },
    VALID: {
      value: 'PAID',
      label: 'Valid',
    },
    CANCELED: {
      value: 'CANCELED',
      label: 'Canceled',
    },
  };

  const guestCheckedInStatusOptions = {
    ALL: {
      value: '',
      label: 'All check ins',
    },
    UNCHECKEDIN: {
      value: 'UNCHECKEDIN',
      label: 'Pending',
    },
    CHECKEDIN: {
      value: 'CHECKEDIN',
      label: 'Checked in',
    },
  };

  useEffect(() => {
    setSearch(query.search || '');
  }, [query.search]);

  useEffect(() => {
    setSortOption(getSortOptionFromQuery(query.sortBy, query.sortDirection));
  }, [query.sortBy, query.sortDirection]);

  useEffect(() => {
    setTicketGroupFilter(query.ticketGroup || 'ALL');
  }, [query.ticketGroup]);

  useEffect(() => {
    setTicketTypeFilter(query.ticketType || 'ALL');
  }, [query.ticketType]);

  useEffect(() => {
    setGuestStatusFilter(query.guestStatus || 'ALL');
  }, [query.guestStatus]);

  useEffect(() => {
    setGuestCheckInStatusFilter(query.guestCheckInStatus || 'ALL');
  }, [query.guestCheckInStatus]);

  useEffect(() => {
    if (ticketTypeFilter !== 'ALL' && !ticketTypeOptions[ticketTypeFilter]) {
      setTicketTypeFilter('ALL');
    }
  }, [ticketGroupFilter, ticketTypeFilter, ticketTypeOptions]);

  const sortBy = SORT_OPTIONS[sortOption].value;
  const sortDirection = SORT_OPTIONS[sortOption].direction;

  const sort = {
    [sortBy]: sortDirection,
  };

  const filter = {
    ...(ticketGroupOptions[ticketGroupFilter]?.value && {
      ticketGroup: ticketGroupOptions[ticketGroupFilter].value,
    }),
    ...(ticketTypeOptions[ticketTypeFilter]?.value && {
      ticketType: ticketTypeOptions[ticketTypeFilter].value,
    }),
    ...(guestStatusOptions[guestStatusFilter]?.value && {
      status: guestStatusOptions[guestStatusFilter].value,
    }),
    ...(guestCheckedInStatusOptions[guestCheckInStatusFilter]?.value && {
      checkedInStatus: guestCheckedInStatusOptions[guestCheckInStatusFilter].value,
    }),
  };

  let title = 'customers';
  if (page.type === 'form') {
    title = 'leads';
  } else if (page.type === 'event') {
    title = 'guests';
  }

  useEffect(() => {
    const params = {};
    params.sortBy = sortBy;
    params.sortDirection = sortDirection;
    if (search) {
      params.search = search;
    }
    if (ticketGroupFilter !== 'ALL' && ticketGroupOptions[ticketGroupFilter]?.value) {
      params.ticketGroup = ticketGroupFilter;
    }
    if (ticketTypeFilter !== 'ALL' && ticketTypeOptions[ticketTypeFilter]?.value) {
      params.ticketType = ticketTypeFilter;
    }
    if (guestStatusFilter !== 'ALL' && guestStatusOptions[guestStatusFilter]?.value) {
      params.guestStatus = guestStatusFilter;
    }
    if (
      guestCheckInStatusFilter !== 'ALL' &&
      guestCheckedInStatusOptions[guestCheckInStatusFilter]?.value
    ) {
      params.guestCheckInStatus = guestCheckInStatusFilter;
    }
    history.replace({
      search: stringify(params),
    });
  }, [
    sortBy,
    sortDirection,
    search,
    ticketGroupFilter,
    ticketTypeFilter,
    guestStatusFilter,
    guestCheckInStatusFilter,
    ticketGroupOptions,
    ticketTypeOptions,
  ]);

  return (
    <div className="mb-24 overflow-auto">
      <div className="flex items-center justify-between pb-4">
        <h2 className="text-xl font-medium">Guests</h2>

        <div className="flex items-center gap-x-2">
          <SearchInput
            onChange={setSearch}
            name="search_customers"
            placeholder="Search by name, email or code"
            className="w-66"
            initialValue={search}
          />

          <FilterInput
            options={ticketGroupOptions}
            onChange={setTicketGroupFilter}
            value={ticketGroupFilter}
          />

          <FilterInput
            options={ticketTypeOptions}
            onChange={setTicketTypeFilter}
            value={ticketTypeFilter}
          />

          <FilterInput
            options={guestStatusOptions}
            onChange={setGuestStatusFilter}
            value={guestStatusFilter}
          />

          <FilterInput
            options={guestCheckedInStatusOptions}
            onChange={setGuestCheckInStatusFilter}
            value={guestCheckInStatusFilter}
          />

          <SortInput options={SORT_OPTIONS} onChange={setSortOption} value={sortOption} />

          <ExportView
            collection="guest-list"
            label={title}
            filters={{
              pageId: page._id,
              ...filter,
            }}
          />
        </div>
      </div>

      <CardContent className="overflow-auto mb-20">
        <CardListItem variant="header" className="!bg-gray-50 !mt-0 rounded-t">
          <CardListItemElement size="fill" align="right">
            Guest
          </CardListItemElement>
          <CardListItemElement size="xl">Ticket type</CardListItemElement>
          <CardListItemElement size="medium">Booking ID</CardListItemElement>
          <CardListItemElement size="large">Ticket code</CardListItemElement>
          <CardListItemElement size="medium">Price</CardListItemElement>
          <CardListItemElement size="xl">Booked on</CardListItemElement>
          <CardListItemElement size="medium">Status</CardListItemElement>
          <CardListItemElement size="large">Checked in</CardListItemElement>
        </CardListItem>

        <GuestsList sort={sort} search={search} filter={filter} />
      </CardContent>
    </div>
  );
};

export default PageGuests;
